/**
 * A leitura do caso, inteira, para auditar (E205.F4.T05, ADR-0147 §2.5): a conferência da leitura
 * (E140.F3, GUS-143), com os pontos do perito, e logo abaixo as informações e os limites da leitura
 * (a ficha, E139.F3, GUS-142), aberta. Mora em "Mais → Informações da leitura"
 * (`InformacoesDaLeituraPage`), fora da mesa: na mesa, a correção de um campo é feita no documento, a
 * situação de cada página está na aba Documentos e o ponto do perito é pendência da aba Pendências.
 *
 * E155.F3 (GUS-192): o ponto do perito abre o cartão "Rever leitura na fonte" na ficha, e a releitura
 * que mudou o exame avisa quem chamou.
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useState } from 'react';

import { ANCORA_DA_FICHA } from '@/components/FichaDaLeitura/AncoraDaFicha';
import {
  FichaDaLeitura,
  type PedidoDeReverLeitura,
} from '@/components/FichaDaLeitura/FichaDaLeitura';

import { ConferenciaDaLeitura } from './ConferenciaDaLeitura';
import type { AlvoDaReleitura } from './PontosDoPerito';

export interface LeituraDoCasoProps {
  client: AxiosInstance;
  caseId: string;
  /** Muda quando o caso é relido: a conferência e a ficha são lidas de novo. */
  releitura?: unknown;
  /** O pedido sobre uma etapa, ou a releitura que mudou o exame: quem chamou relê. */
  onMudou?: () => void;
  /**
   * E205.F4.C02 (F-E205.F4-02): só consulta ("Informações da leitura"): valores, fontes e páginas,
   * sem nenhuma ação que altere o caso — a correção de um campo é feita no documento, na mesa.
   */
  consulta?: boolean;
}

export function LeituraDoCaso({
  client,
  caseId,
  releitura,
  onMudou,
  consulta = false,
}: LeituraDoCasoProps): JSX.Element {
  const [pedido, setPedido] = useState<PedidoDeReverLeitura | null>(null);
  const reverLeitura = useCallback(
    (alvo: AlvoDaReleitura) =>
      setPedido((anterior) => ({ ...alvo, vez: (anterior?.vez ?? 0) + 1 })),
    [],
  );
  return (
    <div className="space-y-sc-6">
      <ConferenciaDaLeitura
        client={client}
        caseId={caseId}
        releitura={releitura}
        onReverLeitura={reverLeitura}
        consulta={consulta}
      />
      {/* E157.F3: a âncora que "Rever leitura de um campo" da tela de certificados aponta. */}
      <div id={ANCORA_DA_FICHA} className="scroll-mt-sc-6">
        <FichaDaLeitura
          client={client}
          caseId={caseId}
          releitura={releitura}
          reverLeitura={pedido}
          onMudou={onMudou}
          consulta={consulta}
        />
      </div>
    </div>
  );
}
